import axios from 'axios';

// Configuración base de Axios para API normal
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Configuración base para endpoints del dashboard (sin /api)
const DASHBOARD_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cliente específico para dashboard
const dashboardClient = axios.create({
  baseURL: DASHBOARD_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para incluir el token en las peticiones
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dashboardToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas de error
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dashboardToken');
      window.location.href = '/dashboard/login';
    }
    return Promise.reject(error);
  }
);

// Servicios de API
export const authService = {
  // Login de líder
  loginLider: async (documentoIdentidad) => {
    try {
      const response = await apiClient.post('/auth/login_lider', {
        documento_identidad: documentoIdentidad
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al iniciar sesión');
    }
  },

  // Login de dashboard con OTP
  loginDashboard: async (username, password) => {
    try {
      const response = await dashboardClient.post('/dashboard/login', {
        username,
        password
      });
      
      // Si no requiere OTP, guardar token directamente
      if (!response.data.requires_otp && response.data.access_token) {
        localStorage.setItem('dashboardToken', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Credenciales incorrectas');
    }
  },

  // Verificar OTP para dashboard
  verifyDashboardOTP: async (userId, otpCode) => {
    try {
      const response = await dashboardClient.post('/dashboard/verify-otp', {
        user_id: userId,
        otp_code: otpCode
      });
      
      // Guardar token después de verificar OTP
      if (response.data.access_token) {
        localStorage.setItem('dashboardToken', response.data.access_token);
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Código OTP inválido');
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('dashboardToken');
  },

  // Verificar si está autenticado para dashboard
  isDashboardAuthenticated: () => {
    return !!localStorage.getItem('dashboardToken');
  },

  // Obtener token del dashboard
  getDashboardToken: () => {
    return localStorage.getItem('dashboardToken');
  },

  // OTP - Verificar si usuario existe
  checkUser: async (cedula) => {
    try {
      const response = await apiClient.get(`/auth/check-user/${cedula}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error verificando usuario');
    }
  },

  // OTP - Solicitar código
  requestOTP: async (cedula) => {
    try {
      const response = await apiClient.post('/auth/request-otp', { cedula });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error solicitando código OTP');
    }
  },

  // OTP - Verificar código
  verifyOTP: async (cedula, otp_code) => {
    try {
      const response = await apiClient.post('/auth/verify-otp', { cedula, otp_code });
      
      // Guardar token en localStorage
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_info', JSON.stringify(response.data.user_info));
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Código de verificación inválido');
    }
  }
};

export const evaluacionService = {
  // Obtener plantilla de evaluación
  getPlantilla: async () => {
    try {
      const response = await apiClient.get('/evaluaciones/plantilla');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al obtener plantilla');
    }
  },

  // Enviar evaluación
  submitEvaluacion: async (evaluacionData) => {
    try {
      const response = await apiClient.post('/evaluaciones/submit', evaluacionData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al enviar evaluación');
    }
  },

  // Obtener opciones de evaluación
  getOpcionesEvaluacion: async (userId, userType) => {
    try {
      const response = await apiClient.get(`/evaluacion/opciones/${userId}/${userType}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al obtener opciones');
    }
  },

  // Servicios para evaluaciones dinámicas
  dinamicas: {
    // Obtener plantilla dinámica
    getPlantillaDinamica: async (tipoEvaluacion) => {
      try {
        const response = await apiClient.get(`/dinamicos/plantilla/${tipoEvaluacion}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al obtener plantilla dinámica');
      }
    },

    // Obtener preguntas por tipo
    getPreguntasByTipo: async (tipoEvaluacion) => {
      try {
        const response = await apiClient.get(`/dinamicos/preguntas/${tipoEvaluacion}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al obtener preguntas');
      }
    },

    // Enviar respuestas dinámicas
    submitRespuestasDinamicas: async (respuestas) => {
      try {
        const response = await apiClient.post('/dinamicos/respuestas/submit', respuestas);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al enviar respuestas');
      }
    },

    // Obtener categorías por tipo
    getCategoriasByTipo: async (tipoEvaluacion) => {
      try {
        const response = await apiClient.get(`/dinamicos/categorias/${tipoEvaluacion}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al obtener categorías');
      }
    },

    // Obtener respuestas de evaluación
    getRespuestasEvaluacion: async (evaluadoId, evaluadorId, tipoEvaluacion) => {
      try {
        const response = await apiClient.get(`/dinamicos/respuestas/${evaluadoId}/${evaluadorId}/${tipoEvaluacion}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al obtener respuestas');
      }
    },

    // Obtener puntuación por categoría
    getPuntuacionPorCategoria: async (evaluadoId, tipoEvaluacion) => {
      try {
        const response = await apiClient.get(`/dinamicos/puntuacion-categoria/${evaluadoId}/${tipoEvaluacion}`);
        return response.data;
      } catch (error) {
        throw new Error(error.response?.data?.detail || 'Error al obtener puntuaciones');
      }
    }
  }
};

export const dashboardService = {
  // Obtener analytics del dashboard
  getAnalytics: async () => {
    try {
      const response = await apiClient.get('/dashboard/analytics');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al obtener datos del dashboard');
    }
  }
};

export default apiClient;
